/**
 * PublicHearingCharts.js — Resolve os marcadores [GRAFICO_*] do Capítulo 14 (Audiência
 * Pública) do PMAU, substituindo-os pelos gráficos Highcharts reais — mesmo padrão de
 * ParticipatoryDiagnosisCharts.js (capítulo 3): renderiza em SVG via chart.getSVG() e
 * insere como <figure> antes de salvar o conteúdo gerado.
 *
 * Os marcadores abaixo espelham os montados em `_stage_public_hearing`
 * (ai-service/app/generation/service.py) — os dois lados precisam ficar em sincronia.
 */

import Highcharts from "highcharts";
import HighchartsExporting from "highcharts/modules/exporting";
import PublicHearingService from "../servico/PublicHearingService";

if (typeof HighchartsExporting === "function") {
  HighchartsExporting(Highcharts);
}

const NAVY = "#16265C";
const BAR_COLOR = "#1B3A6B";

const NEIGHBORHOOD_MARKER = "[GRAFICO_BAIRROS]";
const CHAPTER_MARKER = "[GRAFICO_CONTRIBUICOES_CAPITULO]";
const HAS_MARKER_RE = /\[GRAFICO_(BAIRROS|CONTRIBUICOES_CAPITULO)\]/;

function withViewBox(svg) {
  if (/\sviewBox=/.test(svg)) return svg;
  const wMatch = svg.match(/<svg[^>]*\swidth="(\d+(?:\.\d+)?)"/);
  const hMatch = svg.match(/<svg[^>]*\sheight="(\d+(?:\.\d+)?)"/);
  if (!wMatch || !hMatch) return svg;
  return svg.replace(/<svg\s/, `<svg viewBox="0 0 ${wMatch[1]} ${hMatch[1]}" `);
}

function renderSvg(options, width, height) {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "-9999px";
  container.style.left = "-9999px";
  container.style.width = width + "px";
  container.style.height = height + "px";
  document.body.appendChild(container);
  try {
    const chart = Highcharts.chart(container, options);
    const svg = withViewBox(chart.getSVG());
    chart.destroy();
    return svg;
  } finally {
    document.body.removeChild(container);
  }
}

function figureHtml(svg, caption) {
  return "<figure>" + svg + (caption ? "<figcaption>" + caption + "</figcaption>" : "") + "</figure>";
}

function replaceAll(html, marker, replacement) {
  return html.split(marker).join(replacement);
}

function neighborhoodChartOptions(rows) {
  return {
    chart: { type: "bar", height: Math.max(260, rows.length * 34 + 90), animation: false },
    title: { text: null },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: { categories: rows.map((r) => r.neighborhood), title: { text: null } },
    yAxis: { min: 0, allowDecimals: false, title: { text: null } },
    tooltip: { enabled: false },
    plotOptions: {
      series: { animation: false },
      bar: {
        color: BAR_COLOR,
        dataLabels: { enabled: true, style: { fontWeight: "700", color: NAVY, textOutline: "none" } }
      }
    },
    series: [{ name: "Participantes", data: rows.map((r) => r.count) }]
  };
}

function chapterChartOptions(rows) {
  return {
    chart: { type: "column", height: 380, animation: false },
    title: { text: null },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      categories: rows.map((r) => String(r.order)),
      title: { text: "Capítulo do PMAU" },
      labels: { style: { fontSize: "11px" } }
    },
    yAxis: { min: 0, allowDecimals: false, title: { text: null } },
    tooltip: {
      formatter() {
        return "<b>" + this.point.fullLabel + "</b><br/>" + this.y + " contribuição(ões)";
      }
    },
    plotOptions: {
      series: { animation: false },
      column: {
        color: BAR_COLOR,
        dataLabels: { enabled: true, style: { fontWeight: "700", color: NAVY, textOutline: "none" } }
      }
    },
    series: [{ name: "Contribuições", data: rows.map((r) => ({ y: r.count, fullLabel: r.order + ". " + r.label })) }]
  };
}

/**
 * Substitui [GRAFICO_BAIRROS] e [GRAFICO_CONTRIBUICOES_CAPITULO] pelos gráficos reais do
 * evento de audiência pública mais recente. Nunca lança exceção — em caso de falha, remove
 * o marcador não resolvido para não vazar texto de marcador no capítulo salvo.
 *
 * Retorna { html, failedMarkers }.
 */
export async function resolvePublicHearingCharts(html, eventId, publicToken) {
  if (!html || !HAS_MARKER_RE.test(html)) return { html, failedMarkers: [] };

  let result = html;
  const failedMarkers = [];

  if (result.includes(NEIGHBORHOOD_MARKER)) {
    try {
      const resp = await PublicHearingService.GetRegistrationsByNeighborhood(eventId);
      const rows = resp.data || [];
      if (rows.length === 0) {
        result = replaceAll(result, NEIGHBORHOOD_MARKER, "");
        failedMarkers.push(NEIGHBORHOOD_MARKER);
      } else {
        const svg = renderSvg(neighborhoodChartOptions(rows), 720, Math.max(260, rows.length * 34 + 90));
        result = replaceAll(result, NEIGHBORHOOD_MARKER, figureHtml(svg, "Participantes por bairro"));
      }
    } catch (e) {
      console.error("[PublicHearingCharts] Falha ao renderizar " + NEIGHBORHOOD_MARKER, e);
      result = replaceAll(result, NEIGHBORHOOD_MARKER, "");
      failedMarkers.push(NEIGHBORHOOD_MARKER);
    }
  }

  if (result.includes(CHAPTER_MARKER)) {
    try {
      const resp = await PublicHearingService.GetPanel(publicToken);
      const rows = (resp.data && resp.data.by_chapter) || [];
      if (rows.every((r) => r.count === 0)) {
        result = replaceAll(result, CHAPTER_MARKER, "");
        failedMarkers.push(CHAPTER_MARKER);
      } else {
        const svg = renderSvg(chapterChartOptions(rows), 900, 380);
        result = replaceAll(result, CHAPTER_MARKER, figureHtml(svg, "Contribuições por capítulo do PMAU"));
      }
    } catch (e) {
      console.error("[PublicHearingCharts] Falha ao renderizar " + CHAPTER_MARKER, e);
      result = replaceAll(result, CHAPTER_MARKER, "");
      failedMarkers.push(CHAPTER_MARKER);
    }
  }

  return { html: result, failedMarkers };
}
